<template>
  <form class="form holiday-search"
        :action="action"
        role="search"
        @submit="submit">

    <div class="holiday-search__term-wrapper"
         :class="{ 'blure-effect': openWhen || openHow || openDepartures}">

      <label class="visually-hidden"
             :for="field('holiday-where')">
        {{ whereDescription }}
      </label>
      
      <input class="holiday-search__term"
             :id="field('holiday-where')"
             :name="whereType"
             :placeholder="isClicked ? onclickPlaceholder : whereDescription"
             type="text"
             autocomplete="off"
             :value="where"
             @input="onInput"
             @blur="handleBlur"
             @click.stop="openAutoComplete" />
      
      <div class="holiday-search__autocomplete"
           v-if="autoCompleteOpen && suggestions.length"
           @click.stop>
        <div class="holiday-search__autocomplete-section"
             v-for="(section, sectionIndex) in suggestions"
             :key="section.title + sectionIndex">

          <h4 class="holiday-search__autocomplete-title">
            {{section.title}}
          </h4>

          <ul class="holiday-search__autocomplete-list">
            <li class="holiday-search__autocomplete-item"
                v-for="(suggestion, suggestionIndex) in section.items"
                :key="section.title + suggestion.name + suggestionIndex">
              
              <a class="holiday-search__autocomplete-link"
                 v-if="suggestion.url"
                 :href="suggestion.url"
                 :aria-label="'Go to ' + suggestion.name"
                 :title="'Click to view more details about ' + suggestion.name">
                {{suggestion.name}}
              </a>
              
              <button class="holiday-search__autocomplete-button"
                      @click="setWhereFromAutoComplete(suggestion.name, section.type)"
                      type="button"
                      aria-label="Select autocomplete suggestion"
                      titlel="Select autocomplete suggestion"
                      v-else>
                {{suggestion.name}}
              </button>
              
            </li>
          </ul>

        </div>
      </div>

    </div>

    <div class="holiday-search__term-wrapper"
         :class="{ 'blure-effect': openHow || openDepartures || autoCompleteOpen }">

      <label class="visually-hidden"
             :for="field('holiday-when')">
        {{ whenDescription }}
      </label>

      <input class="holiday-search__term"
            :class="{ 'blure-effect': openWhen }"
            :id="field('select-when')"
            type="text"
            :placeholder="whenDescription"
            autocomplete="off"
            @click.stop="toggleWhen" 
            ref="WhenDropdownInput"
             readonly/>
      
      <DatePicker v-show="openWhen" 
                  :items="dates" 
                  label="When" 
                  name="Dates"/>

    </div>

    <div class="holiday-search__term-wrapper"
         :class="{ 'blure-effect': openWhen || openDepartures || autoCompleteOpen}">

      <label class="visually-hidden"
             :for="field('holiday-how')">
        {{ travelStyleDescription }}
      </label>

      <input class="holiday-search__term"
             :id="field('select-how')"
             type="text"
             :placeholder="travelStyleDescription"
             autocomplete="off"
             @click.stop="toggleHow"
             ref="HowDropdownInput"
             readonly/>

      <Dropdown v-show="openHow" 
                :items="types" 
                label="How" 
                name="Types"/>
      
    </div>

    <div v-if="showDepartureFilter" 
         class="holiday-search__term-wrapper"
         :class="{ 'blure-effect': openWhen || openHow || autoCompleteOpen }">

      <label class="visually-hidden" 
             :for="field('select-departures')">
        {{ departureDescription }}
      </label>

      <input class="holiday-search__term"
             :id="field('select-departures')" 
             type="text" 
             :placeholder="departureDescription"
             autocomplete="off" 
             @click.stop="toggleDepartures" 
             ref="DeparturesDropdownInput" 
             readonly />

      <Dropdown v-show="openDepartures" 
                :items="departures" 
                label="Departures" 
                name="Departs"/>

    </div>

    <input class="btn holiday-search__btn border-radius-none uppercase"
           :class="{ 'blure-effect': openWhen || openHow || openDepartures || autoCompleteOpen }"
           type="submit"
           value="Find my holiday" />

  </form>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { v1 as uuid } from 'uuid';
import { mapFields } from 'vuex-map-fields';
import Dropdown from './Dropdown.vue';
import DatePicker from './DatePicker.vue';

export default {
  props: {
    action: {
      type: String,
      default: ''
    },
    autoCompleteUrl: {
      type: String,
      default: '/umbraco/api/search/Suggest'
    },
    whereName: {
      type: String,
      default: 'SearchTerm'
    },
    whereDescription: {
      type: String,
      default: 'Where would you like to go?'
    },
    onclickPlaceholder: {
      type: String,
      default: 'Type your holiday'
    },
    whenName: {
      type: String,
      default: 'Dates'
    },
    whenDescription: {
      type: String,
      default: 'When would you like to go?'
    },
    howName: {
      type: String,
      default: 'Types'
    },
    travelStyleDescription: {
      type: String,
      default: 'What\'s your travel style?'
    },
    departureName: {
      type: String,
      default: 'Departs'
    },
    departureDescription: {
      type: String,
      default: 'Departure Airport?'
    },
    showDepartureFilter: {
      type: Boolean, 
      default: true
    }
  },

  components: {
    Dropdown,
    DatePicker
  },

  data() {
    return {
      uuid: uuid(),
      autoCompleteOpen: false,
      autoCompleteOpenDepartures: false,
      openDepartures: false,
      openWhen: false,
      openHow: false,
      isClicked: false
    };
  },

  computed: {
    ...mapFields('holidaySearch', ['where', 'when', 'how', 'departure']),
    ...mapState('holidaySearch', [
      'whereType',
      'suggestions',
      'departures',
      'dates',
      'types'
    ]),
    ...mapGetters('holidaySearch', ['autoCompleteGetData'])
  },

  created() {
    this.$store.commit(
      'holidaySearch/setAutoCompleteUrl',
      this.autoCompleteUrl
    );
    this.$store.commit('holidaySearch/setWhereType', this.whereName);
    this.$store.dispatch('holidaySearch/autoComplete');

    // close the auto complete dialog on document click
    document.addEventListener('click', this.closeAutoComplete);
  },

  methods: {
    field(name) {
      return name + this.uuid;
    },

    sanitizeInput(value) {
      // This regex removes any character that is not a letter, digit, space, dash, or ampersand
      return value.replace(/[^a-zA-Z0-9\-& ]/g, '')  // Remove unwanted characters
                  .replace(/\s+/g, ' ')              // Replace multiple spaces with a single space
                  .replace(/^\s+/, '');              // Remove leading space(s)
    },

    onInput(e) {
      const sanitizedValue = this.sanitizeInput(e.target.value);
      this.where = sanitizedValue;
      this.handleUserSearch();
    },

    handleBlur() {
      this.isClicked = false;
    },

    handleUserSearch() {
      this.$store.commit('holidaySearch/setWhereType', this.whereName);
      this.$store
        .dispatch('holidaySearch/autoComplete')
        this.$store.dispatch('holidaySearch/autoComplete')
          .then(() => {
            // Only open autoComplete if the input has 3 or more characters
            const minimumNumberOfInputCharacters = 3;
            this.autoCompleteOpen = this.where.length >= minimumNumberOfInputCharacters ? true : false
          });
    },

    onChangeWhen(event) {
      this.$store.dispatch('holidaySearch/autoComplete');
    },

    onChangeHow(event) {
      this.$store.dispatch('holidaySearch/autoComplete');
    },

    onChangeDeparture(event) {
      this.$store.dispatch('holidaySearch/autoComplete');
    },

    openAutoComplete() {
      this.isClicked = true;
      this.autoCompleteOpen = !this.autoCompleteOpen;
      this.openWhen = false;
      this.openHow = false;
      this.openDepartures = false;
      this.displaySelectedValues();
      this.$emit('toggle-open', this.autoCompleteOpen);
    },

    toggleDepartures() {
      this.openDepartures = !this.openDepartures;
      this.openWhen = false;
      this.openHow = false;
      this.autoCompleteOpen = false;
      this.displaySelectedValues();
      this.$emit('toggle-open', this.openDepartures);
    },

    toggleWhen() {
      this.openWhen = !this.openWhen;
      this.openHow = false;
      this.openDepartures = false;
      this.autoCompleteOpen = false;
      this.displaySelectedValues();
      this.$emit('toggle-open', this.openWhen);
    },

    toggleHow() {
      this.openHow = !this.openHow;
      this.openWhen = false;
      this.openDepartures = false;
      this.autoCompleteOpen = false;
      this.displaySelectedValues();
      this.$emit('toggle-open', this.openHow);
    },

    displaySelectedValues() {
      const HowDropdownInput = this.$refs.HowDropdownInput;
      if (HowDropdownInput) {
        HowDropdownInput.value = this.types
          .filter(type => type.selected)
          .map(type => type.label)
          .join(', ');
      }
      
      const WhenDropdownInput = this.$refs.WhenDropdownInput;
      if (WhenDropdownInput) {
        WhenDropdownInput.value = this.dates
          .filter(date => date.selected)
          .map(date => date.label)
          .join(', ');
      }
      
      const DeparturesDropdownInput = this.$refs.DeparturesDropdownInput;
      if (DeparturesDropdownInput) {
        DeparturesDropdownInput.value = this.departures
          .filter(dep => dep.selected)
          .map(dep => dep.label)
          .join(', ');
      }
    },

    openAutoCompleteDepartures() {
      this.autoCompleteOpenDepartures = true;
    },

    closeAutoComplete() {
      this.autoCompleteOpen = false;
      this.autoCompleteOpenDepartures = false;
      if (this.openDepartures)
        this.toggleDepartures();
      if (this.openWhen)
        this.toggleWhen();
      if (this.openHow)
        this.toggleHow();
      this.$emit('toggle-open', this.autoCompleteOpen);
    },

    setWhereFromAutoComplete(where, whereType) {
      this.where = where;
      this.$store.commit('holidaySearch/setWhereType', whereType);
      this.closeAutoComplete();
      this.$store.dispatch('holidaySearch/autoComplete');
    },

    submit() {
      GTM.searchEvent(this.where, 
            this.dates
              .filter(date => date.selected)
              .map(date => date.label), 
            this.types
              .filter(type => type.selected)
              .map(type => type.label), 
            this.departures
              .filter(dep => dep.selected)
              .map(dep => dep.label));

      GTM.searchEventNew(this.where, 
            this.dates
              .filter(date => date.selected)
              .map(date => date.label), 
            this.types
              .filter(type => type.selected)
              .map(type => type.label), 
            this.departures
              .filter(dep => dep.selected)
              .map(dep => dep.label));
    }
  }
};
</script>